import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert,
  CircularProgress, Box, Typography, MenuItem, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';

// Employment contract for one employee. Everything is pre-filled from their record and working
// schedule; the manager checks it and creates the contract, which is saved to the Document Vault.
//   <ContractDialog open={open} employeeRef={employee.secure_id || employee.id} onClose={...} />
export default function ContractDialog({ open, employeeRef, onClose }) {
  const navigate = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !employeeRef) return;
    let cancelled = false;
    setLoading(true); setError(''); setDone(null); setPrefill(null);
    api.get(`/letter-gen/prefill/employment-contract/${employeeRef}`)
      .then((res) => {
        if (cancelled) return;
        const { warnings, employee_name, employee_address, weekly_hours, schedule, ...editable } = res.data.prefill;
        setPrefill(res.data.prefill);
        setFields(editable);
      })
      .catch((e) => !cancelled && setError(e.response?.data?.error || 'Could not load this employee.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, employeeRef]);

  const tick = (name) => (e) => setFields((f) => ({ ...f, [name]: e.target.checked }));
  const set = (name) => (e) => setFields((f) => ({ ...f, [name]: e.target.value }));

  const handleGenerate = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.post(`/letter-gen/generate/employment-contract/${employeeRef}`, fields);
      setDone(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create the contract.');
    } finally {
      setSaving(false);
    }
  };

  const text = (name, label, extra = {}) => (
    <TextField fullWidth size="small" label={label} value={fields[name] ?? ''} onChange={set(name)} {...extra} />
  );
  const date = (name, label, extra = {}) => text(name, label, { type: 'date', InputLabelProps: { shrink: true }, ...extra });
  const select = (name, label, options) => (
    <TextField select fullWidth size="small" label={label} value={fields[name] ?? ''} onChange={set(name)}>
      {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </TextField>
  );

  const blocked = !prefill || !prefill.employee_address || !(prefill.weekly_hours > 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Employment contract{prefill ? ` - ${prefill.employee_name}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {done ? (
          <Alert severity="success">
            {done.message}.{' '}
            <Button size="small" onClick={() => { onClose(); navigate('/documents'); }}>Open Document Vault</Button>
          </Alert>
        ) : prefill && (
          <>
            {prefill.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These details come from the employee's record and working schedule. Check them, then create
              the contract; it is saved to your Document Vault to review and sign. Probation, notice
              periods, pension and pay frequency come from your Document Settings.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{text('job_title', 'Job title')}</Grid>
              <Grid item xs={12} sm={6}>{text('reporting_manager', 'Reports to')}</Grid>
              <Grid item xs={12} sm={4}>{date('start_date', 'Start date')}</Grid>
              <Grid item xs={12} sm={4}>
                {date('continuous_service_date', 'Continuous employment began', {
                  helperText: 'Change only if earlier service counts (for example a business transfer).',
                })}
              </Grid>
              <Grid item xs={12} sm={4}>{text('hourly_rate', 'Hourly rate (£)')}</Grid>
              <Grid item xs={12} sm={4}>{text('annual_leave_days', 'Holiday days a year (including bank holidays)')}</Grid>
              <Grid item xs={12} sm={4}>{select('hours_type', 'Hours', ['full-time', 'part-time'])}</Grid>
              <Grid item xs={12} sm={4}>{select('contract_term', 'Contract', ['permanent', 'fixed-term'])}</Grid>
              {fields.contract_term === 'fixed-term' && (
                <Grid item xs={12} sm={4}>{date('end_date', 'Fixed term ends')}</Grid>
              )}
              <Grid item xs={12}>{text('commission_structure', 'Commission (write None if there is none)')}</Grid>
            </Grid>


            <Typography variant="subtitle2" sx={{ mt: 3 }}>Restrictive covenants (optional)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Tick only the restrictions you want for this employee. Unticked ones are shown in the contract as not applying. The periods come from your Document Settings.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel control={<Checkbox checked={!!fields.covenant_non_compete} onChange={tick('covenant_non_compete')} />} label="Non-competition" />
              <FormControlLabel control={<Checkbox checked={!!fields.covenant_non_solicit} onChange={tick('covenant_non_solicit')} />} label="Non-solicitation of clients and staff" />
              <FormControlLabel control={<Checkbox checked={!!fields.covenant_non_dealing} onChange={tick('covenant_non_dealing')} />} label="Non-dealing with clients" />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 3 }}>
              Working hours: {prefill.weekly_hours} a week (from the Schedule tab)
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow><TableCell>Day</TableCell><TableCell>Start</TableCell><TableCell>Finish</TableCell><TableCell>Hours</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {prefill.schedule.map((r) => (
                  <TableRow key={r.day}>
                    <TableCell>{r.day}</TableCell>
                    <TableCell>{r.working ? r.start : 'Day off'}</TableCell>
                    <TableCell>{r.working ? r.end : '-'}</TableCell>
                    <TableCell>{r.working ? r.hours : 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Address on the contract: {prefill.employee_address || 'none on file'}. To change the hours or address, use the Schedule or Personal Info tab.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button variant="contained" onClick={handleGenerate} disabled={saving || loading || blocked}>
            {saving ? 'Creating...' : 'Create contract'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
